"use client"

import { useState, useEffect, useRef } from "react"
import { AlertTriangle, Info, XCircle, ChevronDown } from "lucide-react"
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert"
import { cn } from "@/lib/utils"
import { usePersistedState } from "@/hooks/use-persisted-state"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useLanguage } from "@/context/language-context"
import { useTranslation } from "@/hooks/use-translation"
import { getLocalizedField } from "@/utils/language-utils"
import type { MaintenanceMessage } from "@/types/workflow"

// Matches the stage-counts cadence (see useStageCountsInit). The banner refreshes
// itself so it can appear/disappear during an already-open session without a reload.
const POLL_MS = 5 * 60 * 1000
// How often we re-evaluate the Start/End window so the banner flips on/off (and switches
// between the upcoming/active states) at the window edges between polls.
const TICK_MS = 30 * 1000
// localStorage key holding the signature of the (message, phase) the user last collapsed.
const COLLAPSED_KEY = "wfh_maintenance_banner_collapsed"
// Default lead time for the "upcoming" notice when NotifyHoursBefore isn't configured.
const DEFAULT_NOTIFY_HOURS = 24
// Default active-state duration when no EndDateTime is configured.
const DEFAULT_WINDOW_HOURS = 48

const HOUR_MS = 60 * 60 * 1000

type BannerKind = "info" | "warning" | "error"
type Phase = "upcoming" | "active"

const iconFor = (kind: BannerKind) =>
  kind === "error" ? XCircle : kind === "info" ? Info : AlertTriangle

const kindFor = (type: string): BannerKind =>
  type === "error" ? "error" : type === "info" ? "info" : "warning"

// Header wording follows the kind (it isn't always "maintenance").
const titleKeyFor = (kind: BannerKind) =>
  kind === "error" ? "bannerTitleError" : kind === "info" ? "bannerTitleInfo" : "bannerTitleWarning"

// Collapsed-chip colours per kind — mirror the Alert variants in ui/alert.tsx so
// the pill matches the expanded banner's Info (blue) / Warning (amber) / Error (red).
const chipClassFor = (kind: BannerKind): string =>
  ({
    info: "border-sky-300/70 bg-sky-50 text-sky-900 hover:bg-sky-100 dark:border-sky-400/50 dark:bg-sky-950/40 dark:text-sky-100 dark:hover:bg-sky-900/40",
    warning:
      "border-amber-400/60 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-400/60 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-900/40",
    error:
      "border-red-300/70 bg-red-50 text-red-900 hover:bg-red-100 dark:border-red-500/50 dark:bg-red-950/40 dark:text-red-100 dark:hover:bg-red-900/40",
  }[kind])

// Parse a backend timestamp as an absolute instant. The backend serializes DateTimeOffset
// (explicit offset), but — matching audit-history-panel.tsx — defensively treat a value with
// NO timezone designator as UTC, so a hand-authored "2026-07-15T02:00:00" isn't read as the
// viewer's local time (which would shift the window by their UTC offset).
const parseInstant = (s?: string | null): Date | null => {
  if (!s) return null
  const hasTz = /(?:Z|[+-]\d{2}:?\d{2})$/.test(s)
  const d = new Date(hasTz ? s : `${s}Z`)
  return isNaN(d.getTime()) ? null : d
}

const localeTag = (language: string) => (language === "fr" ? "fr-FR" : "en-US")

// Short zone abbreviation for the viewer's local zone (e.g. "EST", "PDT").
const tzAbbrev = (d: Date, language: string): string =>
  d.toLocaleTimeString(localeTag(language), { timeZoneName: "short" }).split(" ").pop() ?? ""

// "Jul 14, 9:00 PM EST" — viewer's local time, always with the zone so it's unambiguous.
const fmtDateTime = (d: Date, language: string): string => {
  const tag = localeTag(language)
  const date = d.toLocaleDateString(tag, { month: "short", day: "numeric" })
  const time = d.toLocaleTimeString(tag, { hour: "numeric", minute: "2-digit", hour12: language !== "fr" })
  return `${date}, ${time} ${tzAbbrev(d, language)}`
}

// "Jul 14, 9:00 PM – 12:00 AM EST" (date shown once when same local day, else on both ends).
const fmtRange = (start: Date, end: Date, language: string): string => {
  const tag = localeTag(language)
  const timeOpts: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit", hour12: language !== "fr" }
  const startDate = start.toLocaleDateString(tag, { month: "short", day: "numeric" })
  const startTime = start.toLocaleTimeString(tag, timeOpts)
  const endTime = end.toLocaleTimeString(tag, timeOpts)
  const tz = tzAbbrev(end, language)
  const sameDay = start.toLocaleDateString(tag) === end.toLocaleDateString(tag)
  if (sameDay) return `${startDate}, ${startTime} – ${endTime} ${tz}`
  const endDate = end.toLocaleDateString(tag, { month: "short", day: "numeric" })
  return `${startDate}, ${startTime} – ${endDate}, ${endTime} ${tz}`
}

// Identity of what's being shown (content + phase, not the live clock). Collapse is remembered
// against this, so a refresh keeps it collapsed — but editing the copy/type/window, or the
// upcoming→active transition, produces a new signature and re-expands ("reactivation").
const signatureOf = (m: MaintenanceMessage, phase: Phase): string =>
  [phase, m.type ?? "", m.messageEnglish ?? "", m.messageFrench ?? "", m.upcomingMessageEnglish ?? "", m.upcomingMessageFrench ?? "", m.startDateTime ?? "", m.endDateTime ?? "", m.notifyHoursBefore ?? ""].join("|")

/**
 * Reusable maintenance banner driven by WorkflowHubConfiguration.maintenanceMessage. Two states
 * off one configured window: an Info "upcoming" notice (from Start − NotifyHoursBefore until Start)
 * and the type-styled "active" message (Start until End, defaulting to Start + 48h). It reads the
 * config loaded on mount and independently re-fetches every 5 min — NOT via the global config
 * reducer — so polling never resets the user's grid state. Collapses to a compact chip (never fully
 * dismissed); collapsed state persists per (message, phase) signature in localStorage.
 */
export function MaintenanceBanner() {
  const { configuration } = useWorkflow()
  const { fetchWorkflowConfiguration } = useWorkflowApi()
  const { language } = useLanguage()
  const { t } = useTranslation()

  const [message, setMessage] = useState<MaintenanceMessage | undefined>(
    configuration?.maintenanceMessage
  )
  // Signature of the (message, phase) the user collapsed; null means "show expanded".
  const [collapsedSig, setCollapsedSig] = usePersistedState<string | null>(COLLAPSED_KEY, null)
  const [now, setNow] = useState(() => Date.now())

  // Keep in sync when the global config reloads (mount, view switch).
  useEffect(() => {
    setMessage(configuration?.maintenanceMessage)
  }, [configuration?.maintenanceMessage])

  // Independent 5-min poll via a ref so the interval always calls the latest fetcher.
  const fetchRef = useRef(fetchWorkflowConfiguration)
  useEffect(() => {
    fetchRef.current = fetchWorkflowConfiguration
  }, [fetchWorkflowConfiguration])
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const cfg = await fetchRef.current()
        setMessage(cfg?.maintenanceMessage)
      } catch {
        // Transient fetch failure — keep the last known state until the next tick.
      }
    }, POLL_MS)
    return () => clearInterval(id)
  }, [])

  // Re-evaluate the window edges without waiting for the next poll.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [])

  if (!message?.isEnabled) return null

  const start = parseInstant(message.startDateTime)
  const endRaw = parseInstant(message.endDateTime)
  const effectiveEnd =
    endRaw ?? (start ? new Date(start.getTime() + DEFAULT_WINDOW_HOURS * HOUR_MS) : null)

  // Phase resolution: past the window → hidden; before the start → upcoming (once inside the
  // lead time) else hidden; otherwise active. No start → active immediately (legacy behaviour).
  let phase: Phase
  if (effectiveEnd && now > effectiveEnd.getTime()) {
    return null
  } else if (start && now < start.getTime()) {
    const leadMs = (message.notifyHoursBefore ?? DEFAULT_NOTIFY_HOURS) * HOUR_MS
    if (now < start.getTime() - leadMs) return null
    phase = "upcoming"
  } else {
    phase = "active"
  }

  // Upcoming is always an Info "Notice"; active is styled by the configured Type.
  const kind: BannerKind = phase === "upcoming" ? "info" : kindFor((message.type || "Warning").toLowerCase())
  const Icon = iconFor(kind)
  const title = t(titleKeyFor(kind))

  let body: string
  let chipSuffix: string
  let metaLine: string | null

  if (phase === "upcoming") {
    // Configured upcoming copy wins (either language); otherwise the default localized template.
    const customUpcoming =
      getLocalizedField(message, "upcomingMessage", language) ||
      message.upcomingMessageEnglish ||
      message.upcomingMessageFrench ||
      ""
    body = (customUpcoming || t("upcomingMaintenanceBody")).replace(
      /\{time\}/g,
      effectiveEnd ? fmtDateTime(effectiveEnd, language) : ""
    )
    chipSuffix = t("scheduledMaintenanceUpcoming")
    metaLine = start && effectiveEnd ? t("scheduledFor", { window: fmtRange(start, effectiveEnd, language) }) : null
  } else {
    // Prefer the user's language, but fall back to the other so a single-language (e.g.
    // English-only) message still shows rather than rendering blank.
    const rawText =
      getLocalizedField(message, "message", language) ||
      message.messageEnglish ||
      message.messageFrench ||
      ""
    body = rawText.replace(/\{time\}/g, effectiveEnd ? fmtDateTime(effectiveEnd, language) : "")
    chipSuffix = t("maintenanceInProgress")
    const parts: string[] = []
    if (start) parts.push(t("inEffectSince", { time: fmtDateTime(start, language) }))
    if (effectiveEnd) parts.push(t("fullAccessExpectedBy", { time: fmtDateTime(effectiveEnd, language) }))
    metaLine = parts.length ? parts.join(" · ") : null
  }

  if (!body.trim()) return null

  const sig = signatureOf(message, phase)
  const collapsed = collapsedSig === sig

  // Collapsed: a centered chip that stays visible without taking a full row.
  if (collapsed) {
    return (
      <div className="mb-4 flex justify-center">
        <button
          type="button"
          onClick={() => setCollapsedSig(null)}
          aria-label={t("expandMaintenanceMessage")}
          className={cn(
            "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium transition-colors",
            chipClassFor(kind)
          )}
        >
          <Icon className="h-4 w-4" />
          {`${title}: ${chipSuffix}`}
        </button>
      </div>
    )
  }

  // Expanded: a centered, content-width card (never edge-to-edge). The whole card is clickable
  // to collapse (users don't have to hit the chevron); the chevron stays as a keyboard-focusable
  // affordance. Shows a bold type title, the message, and the schedule/window line.
  return (
    <div className="mb-4 flex justify-center">
      <Alert
        variant={kind}
        onClick={() => setCollapsedSig(sig)}
        title={t("collapseMaintenanceMessage")}
        className="relative w-auto max-w-2xl cursor-pointer pr-10"
        role="status"
        aria-live="polite"
      >
        <Icon className="h-4 w-4" />
        <AlertTitle className="font-bold">{title}</AlertTitle>
        <AlertDescription>{body}</AlertDescription>
        {metaLine && <div className="mt-1 text-xs opacity-80">{metaLine}</div>}
        <button
          type="button"
          onClick={() => setCollapsedSig(sig)}
          aria-label={t("collapseMaintenanceMessage")}
          className="absolute right-2 top-2 rounded p-1 opacity-70 transition-colors hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </Alert>
    </div>
  )
}
